import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import PendingInvites from "@/components/events/PendingInvites";
import { useAuth } from "@/app/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Users, Search, UserRound, UserPlus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

// Define types for contacts and invites
interface Contact {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  type: "friend" | "family";
}

interface Invite {
  id: string;
  recipient_email: string;
  relationship: string;
  created_at: string;
  status: string;
  sender_id: string;
  updated_at: string;
}

interface FriendsAndFamilyProps {
  initialTab?: "friends" | "family" | "all";
}

const FriendsAndFamily = ({ initialTab = "all" }: FriendsAndFamilyProps) => {
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const [inviteEmail, setInviteEmail] = useState("");
  const [relationship, setRelationship] = useState("friend");
  const [isInviting, setIsInviting] = useState(false);
  const [activeFilter, setActiveFilter] = useState<"all" | "friends" | "family">(initialTab);
  const [searchQuery, setSearchQuery] = useState("");
  const [sentInvites, setSentInvites] = useState<Invite[]>([]);
  const [inviteToDelete, setInviteToDelete] = useState<Invite | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Contacts state
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch contacts and sent invites
  useEffect(() => {
    const fetchContactsAndInvites = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      try {
        console.log("Fetching contacts for user:", user.id);
        
        // Fetch real contacts from Supabase
        const { data: contactsData, error: contactsError } = await supabase
          .from('ft_contacts')
          .select('*')
          .eq('user_id', user.id);
          
        if (contactsError) {
          console.error("Error fetching contacts:", contactsError);
        } else {
          // Map the database contacts to our Contact interface
          const mappedContacts = contactsData ? contactsData.map((contact: any) => ({
            id: contact.id,
            name: contact.name || contact.email.split('@')[0],
            email: contact.email,
            type: contact.relationship as "friend" | "family",
            avatar: contact.avatar
          })) : [];
          
          setContacts(mappedContacts);
        }
        
        // Fetch sent invites from Supabase
        const { data: invitesData, error } = await supabase
          .from('ft_friend_invites')
          .select('*')
          .eq('sender_id', user.id);
          
        if (error) {
          console.error("Error fetching invites:", error);
          return;
        }
        
        if (invitesData) {
          console.log("Fetched invites:", invitesData);
          setSentInvites(invitesData as Invite[]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };
    
    fetchContactsAndInvites();
  }, [user]);
  
  // Update filtered contacts whenever contacts, activeFilter, or searchQuery changes
  useEffect(() => {
    const applyFilters = () => {
      const filtered = contacts.filter(contact => {
        const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                             contact.email.toLowerCase().includes(searchQuery.toLowerCase());
        
        if (activeFilter === "all") return matchesSearch;
        return matchesSearch && contact.type === activeFilter;
      });
      
      setFilteredContacts(filtered);
    };
    
    applyFilters();
  }, [contacts, activeFilter, searchQuery]);
  
  const handleInviteFriend = async () => {
    if (!inviteEmail) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }
    
    if (!inviteEmail.includes('@')) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }
    
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to invite friends",
        variant: "destructive",
      });
      return;
    }
    
    setIsInviting(true);
    
    try {
      console.log("Sending invite to:", inviteEmail, "as", relationship);
      
      // Store the invitation in Supabase
      const { data: inviteData, error: inviteError } = await supabase
        .from('ft_friend_invites')
        .insert({
          sender_id: user.id,
          recipient_email: inviteEmail,
          relationship: relationship,
          status: 'pending'
        })
        .select();
      
      if (inviteError) {
        console.error("Error storing invitation:", inviteError);
        toast({
          title: "Error",
          description: "Failed to send invitation. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      // Also store in notifications table for UI updates
      const { error: notificationError } = await supabase
        .from('ft_notifications')
        .insert({
          user_id: user.id,
          message: `You invited ${inviteEmail} to join as a ${relationship}`,
          type: "invite_sent",
          is_read: false,
          status: 'unread',
          action_url: '/friends-and-family'
        });
      
      if (notificationError) {
        console.error("Error storing notification:", notificationError);
      }
      
      // Add to local state
      if (inviteData && inviteData.length > 0) {
        console.log("Invite created:", inviteData[0]);
        setSentInvites([...sentInvites, inviteData[0] as Invite]);
      }
      
      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${inviteEmail} as a ${relationship}`,
      });
      setInviteEmail("");
    } catch (err) {
      console.error("Error sending invitation:", err);
      toast({
        title: "Error",
        description: "Failed to send invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  // Confirm invite deletion
  const confirmDeleteInvite = (invite: Invite) => {
    setInviteToDelete(invite);
    setConfirmDialogOpen(true);
  };
  
  // Handle invite deletion
  const handleDeleteInvite = async () => {
    if (!inviteToDelete || !user) return;
    
    setIsDeleting(true);
    
    try {
      // Delete the invitation from Supabase
      const { error } = await supabase
        .from('ft_friend_invites')
        .delete()
        .eq('id', inviteToDelete.id)
        .eq('sender_id', user.id);
        
      if (error) {
        console.error("Error deleting invitation:", error);
        toast({
          title: "Error",
          description: "Failed to delete invitation. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      // Update local state
      setSentInvites(sentInvites.filter(invite => invite.id !== inviteToDelete.id));
      
      // Add notification about deletion
      const { error: notificationError } = await supabase
        .from('ft_notifications')
        .insert({
          user_id: user.id,
          message: `You canceled the invitation to ${inviteToDelete.recipient_email}`,
          type: "invite_canceled",
          is_read: false,
          status: 'unread',
          action_url: '/friends-and-family'
        });
      
      if (notificationError) {
        console.error("Error storing notification:", notificationError);
      }
      
      toast({
        title: "Invitation canceled",
        description: `The invitation to ${inviteToDelete.recipient_email} has been canceled`,
      });
    } catch (err) {
      console.error("Error deleting invitation:", err);
      toast({
        title: "Error",
        description: "Failed to delete invitation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setConfirmDialogOpen(false);
      setInviteToDelete(null);
    }
  };

  // Handle contact type change
  const handleContactTypeChange = async (contactId: string, newType: "friend" | "family") => {
    if (!user) return;
    
    try {
      // Update the contact in Supabase
      const { error } = await supabase
        .from('ft_contacts')
        .update({ relationship: newType })
        .eq('id', contactId)
        .eq('user_id', user.id);
        
      if (error) {
        console.error("Error updating contact:", error);
        toast({
          title: "Error",
          description: "Failed to update contact. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      // Update the contact type in the local state
      const updatedContacts = contacts.map(contact => 
        contact.id === contactId ? {...contact, type: newType} : contact
      );
      
      setContacts(updatedContacts);
      
      toast({
        title: "Contact updated",
        description: `Contact relationship changed to ${newType}`,
      });
    } catch (err) {
      console.error("Error updating contact:", err);
      toast({
        title: "Error",
        description: "Failed to update contact. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  // Handle contact removal
  const handleRemoveContact = async (contactId: string, contactName: string) => {
    if (!user) return;
    
    try {
      // Delete the contact from Supabase
      const { error } = await supabase
        .from('ft_contacts')
        .delete()
        .eq('id', contactId)
        .eq('user_id', user.id);
        
      if (error) {
        console.error("Error removing contact:", error);
        toast({
          title: "Error",
          description: "Failed to remove contact. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      // Remove the contact from the local state
      setContacts(contacts.filter(contact => contact.id !== contactId));
      
      toast({
        title: "Contact removed",
        description: `${contactName} has been removed from your contacts`,
      });
    } catch (err) {
      console.error("Error removing contact:", err);
      toast({
        title: "Error",
        description: "Failed to remove contact. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-black">Friends & Family</h1>
          <p className="text-muted-foreground">Manage your social connections</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={activeFilter === "friends" ? "default" : "outline"} 
            onClick={() => setActiveFilter("friends")}
            className={activeFilter === "friends" ? "bg-blue-600 hover:bg-blue-700" : ""}
          >
            <span className={activeFilter === "friends" ? "text-white" : "text-black"}>Friends</span>
          </Button>
          <Button 
            variant={activeFilter === "family" ? "default" : "outline"} 
            onClick={() => setActiveFilter("family")}
            className={activeFilter === "family" ? "bg-blue-600 hover:bg-blue-700" : ""}
          >
            <span className={activeFilter === "family" ? "text-white" : "text-black"}>Family</span>
          </Button>
          <Button 
            variant={activeFilter === "all" ? "default" : "outline"} 
            onClick={() => setActiveFilter("all")}
            className={activeFilter === "all" ? "bg-blue-600 hover:bg-blue-700" : ""}
          >
            <span className={activeFilter === "all" ? "text-white" : "text-black"}>All Contacts</span>
          </Button>
        </div>
      </div>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-black">Invite People</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="friend@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1"
            />
            <Select value={relationship} onValueChange={setRelationship}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Relationship" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="friend">Friend</SelectItem>
                <SelectItem value="family">Family</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              onClick={handleInviteFriend} 
              disabled={isInviting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Mail className="mr-2 h-4 w-4" />
              {isInviting ? "Sending..." : "Send Invite"}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Show pending invites section at the top of the page */}
      {isAuthenticated && <PendingInvites />}
      
      {/* Sent invites section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-black">Sent Invitations</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-2">
              {sentInvites.length > 0 ? (
                sentInvites.map((invite) => (
                  <Card key={invite.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <Avatar>
                            <AvatarFallback>{invite.recipient_email.charAt(0).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm text-black">{invite.recipient_email}</p>
                            <Badge variant={invite.relationship === "family" ? "destructive" : "default"} className="mt-1">
                              <span className="text-black">Invited as {invite.relationship}</span>
                            </Badge>
                            <p className="text-xs text-gray-500 mt-1">
                              Sent {new Date(invite.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={invite.status === "pending" ? "outline" : "default"}>
                            <span className="text-black">{invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}</span>
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => confirmDeleteInvite(invite)}
                            disabled={isDeleting}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">No invitations sent</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Contacts list with search */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-black">
            <span>My Contacts</span>
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center p-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredContacts.length > 0 ? (
                filteredContacts.map((contact) => (
                  <div key={contact.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <Avatar>
                        <AvatarFallback>{contact.name.charAt(0).toUpperCase()}</AvatarFallback>
                        {contact.avatar && <AvatarImage src={contact.avatar} alt={contact.name} />}
                      </Avatar>
                      <div>
                        <p className="font-medium text-black">{contact.name}</p>
                        <p className="text-sm text-muted-foreground">{contact.email}</p>
                        <Badge variant={contact.type === "family" ? "destructive" : "default"} className="mt-1">
                          <span className="text-black">{contact.type.charAt(0).toUpperCase() + contact.type.slice(1)}</span>
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select 
                        value={contact.type} 
                        onValueChange={(value) => handleContactTypeChange(contact.id, value as "friend" | "family")}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="friend">Friend</SelectItem>
                          <SelectItem value="family">Family</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleRemoveContact(contact.id, contact.name)}
                      >
                        <span className="text-black">Remove</span>
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <UserRound className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-4 text-lg font-medium text-black">No contacts found</h3>
                  <p className="mt-2 text-muted-foreground">
                    {searchQuery ? "Try a different search term" : "Invite friends and family to get started"}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Delete confirmation dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the invitation sent to {inviteToDelete?.recipient_email}.
              The recipient will no longer be able to accept this invitation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteInvite}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete Invitation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FriendsAndFamily;
